import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Toaster } from "./ui/sonner";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, Disc3, Trash2, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LibraryPage() {
  const { user, loading, openAuth } = useAuth();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("search");
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/api/library`, { credentials: "include" })
      .then((r) => r.json())
      .then(setItems)
      .catch(() => setItems([]));
  }, [user]);

  const open = (item) => {
    sessionStorage.setItem("pg_restore", JSON.stringify(item));
    navigate("/");
  };

  const remove = async (id) => {
    await fetch(`${API}/api/library/${id}`, { method: "DELETE", credentials: "include" });
    setItems((its) => its.filter((i) => i.id !== id));
    toast.success("Removed from library");
  };

  const filtered = (items || []).filter((i) => i.kind === tab);

  return (
    <div className="grain min-h-screen bg-[#09090B] text-white">
      <Toaster theme="dark" position="bottom-right" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link data-testid="library-back-link" to="/" className="flex items-center gap-2 text-sm text-white/60 transition-colors duration-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to search
          </Link>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">My Library</span>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-4xl px-6 pb-24 pt-10">
        {loading ? (
          <div className="flex justify-center pt-20"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>
        ) : !user ? (
          <div className="pt-20 text-center">
            <p className="text-white/50">Sign in to see your saved searches and chains.</p>
            <button data-testid="library-login-btn" onClick={openAuth}
                    className="mt-4 rounded-lg bg-cyan-500 px-6 py-2.5 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400">
              Sign in
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              {[
                { key: "search", label: "Searches", Icon: Bookmark },
                { key: "chain", label: "Chains", Icon: Disc3 },
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  data-testid={`library-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors duration-200 ${
                    tab === key ? "bg-cyan-500 text-black" : "bg-white/5 text-white/50 hover:text-white"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" /> {label} ({(items || []).filter((i) => i.kind === key).length})
                </button>
              ))}
            </div>

            <div className="mt-6 space-y-3">
              {items === null && <Loader2 className="h-5 w-5 animate-spin text-cyan-400" />}
              {items !== null && filtered.length === 0 && (
                <div className="pt-12 text-center">
                  <p className="font-mono text-xs uppercase tracking-widest text-white/25">
                    Nothing saved yet
                  </p>
                  <Link to="/" data-testid="library-empty-cta"
                        className="mt-4 inline-block rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-5 py-2 text-sm font-semibold text-cyan-300 transition-colors duration-200 hover:bg-cyan-500/20">
                    {tab === "chain" ? "Run a Sound Match and save the chain" : "Run a search and save it"}
                  </Link>
                </div>
              )}
              {filtered.map((item) => (
                <div key={item.id} data-testid="library-item"
                     className="flex items-center justify-between gap-4 rounded-md border border-white/10 bg-[#141417] px-5 py-4">
                  <div className="min-w-0">
                    <div className="truncate font-heading text-sm font-bold">
                      {item.kind === "chain" ? "💿 " : ""}{item.query}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-white/40">
                      {item.results?.length || 0} plugins · saved {new Date(item.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button data-testid="library-open-btn" onClick={() => open(item)}
                            className="rounded-lg bg-cyan-500 px-4 py-1.5 text-xs font-semibold text-black transition-colors duration-200 hover:bg-cyan-400">
                      Open
                    </button>
                    <button data-testid="library-delete-btn" onClick={() => remove(item.id)}
                            className="rounded-lg border border-white/10 p-2 text-white/40 transition-colors duration-200 hover:border-red-500/40 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
